import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

import { useChatStore } from 'stores/chat';
import useChatTextStore from 'stores/chat-text';
import useMiniGamesStore from 'stores/miniGames';
import useBreakpoint, { DESKTOP } from 'hooks/useBreakpoint';
import { text } from 'polyglot/polyglot';
import { shallow } from 'zustand/shallow';
import useUserStore from 'stores/user';
import ChatManager from 'socket/ChatManager';
import api from 'utils/api';

import Spinner from 'components/Spinner/Spinner';
import ArrowHead from 'themes/common/svg/arrowhead-no-fill.svg';
import ChatIcon from 'themes/common/svg/chat-icon.svg';
import Scrollbars from 'components/Scrollbars/Scrollbars';
import Button from 'components/Button/Button';
import BingoRooms from 'components/BingoRooms/BingoRooms';
import ToggleButton from './ToggleButton/ToggleButton';
import GroupChatHeader from './GroupChatHeader/GroupChatHeader';
import PrivateChatHeader from './PrivateChatHeader/PrivateChatHeader';
import MessageList from './MessageList/MessageList';
import RoomList from './RoomList/RoomList';
import MiniGamesList from './MiniGamesList/MiniGamesList';

import styles from './Chat.module.scss';
import PrivateChatReqModal from './PrivateChatReqModal/PrivateChatReqModal';
import PrivateChatUserBlockModal from './PrivateChatUserBlockModal/PrivateChatUserBlockModal';

const userSelector = (state) => [state.user, state.setUser];
const currentRoomSelector = (state) => [
  state.currentRoom,
  state.setCurrentRoom,
];
const expandedSelector = (state) => [
  state.isChatExpanded,
  state.setIsChatExpanded,
];
const chatroomsSelector = (state) => state.chatrooms;
const activeRequestsSelector = (state) => state.activeChatRequest;
const privateChatUserBlockModalMsgSelector = (state) =>
  state.privateChatUserBlockModalMsg;
const removeChatroomSelector = (state) => state.removeChatroom;
const lastUpdatedSelector = (state) => state.setLastUpdated;
const resetUnreadCountSelector = (state) => state.resetUnreadCount;
const chatIsClosedSelector = (state) => state.setGroupChatIsClosed;
const bingoRoomsIsVisibleSelector = (state) => [
  state.bingoRoomsIsVisible,
  state.setBingoRoomsVisibility,
];
const miniGamesSelector = (state) => state.miniGameListId;

const CHAT_LOADING_ID = '__chat-loading__';

function Chat() {
  const [isExpanded, setIsChatExpanded] = useChatStore(
    expandedSelector,
    shallow
  );
  const activeRequests = useChatStore(activeRequestsSelector);
  const privateChatUserBlockModalMsg = useChatStore(
    privateChatUserBlockModalMsgSelector
  );
  const setGroupChatIsClosed = useChatTextStore(chatIsClosedSelector);

  const breakpoint = useBreakpoint();
  const isDesktop = breakpoint === DESKTOP;
  const innerRef = useRef(null);
  const chatManager = useRef();

  const [user, setUser] = useUserStore(userSelector, shallow);
  const [currentRoom, setCurrentRoom] = useChatStore(
    currentRoomSelector,
    shallow
  );
  const rooms = useChatStore(chatroomsSelector);
  const removeChatroom = useChatStore(removeChatroomSelector);
  const setLastUpdated = useChatStore(lastUpdatedSelector);
  const resetUnreadCount = useChatStore(resetUnreadCountSelector);

  const [hasUserList, setHasUserList] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [bingoRoomsIsVisible, setBingoRoomsVisibility] = useChatStore(
    bingoRoomsIsVisibleSelector,
    shallow
  );
  const miniGameListId = useMiniGamesStore(miniGamesSelector);
  const [numberOfBingoRooms, setNumberOfBingoRooms] = useState(0);

  const onToggleMenu = () => setIsChatExpanded(!isExpanded);

  const onClickRoom = (room) => {
    setCurrentRoom(room);
    if (bingoRoomsIsVisible) setBingoRoomsVisibility(false);
    if (hasUserList) setHasUserList(false);
  };

  const onClickReturn = (id) => {
    const groupRoom = rooms.find((r) => !r.isPrivate);
    if (!groupRoom && !groupRoom?.roomId) {
      setCurrentRoom(groupRoom);
    } else {
      setCurrentRoom(rooms[0]);
    }
    chatManager.current.leaveChatRoom(id, user.nickname);
    removeChatroom(id);
  };

  const isMinigamesTab = currentRoom?.isMinigamesTab === true;

  useEffect(() => {
    if (!currentRoom && (user.chatId || user.room)) {
      setCurrentRoom({ roomId: CHAT_LOADING_ID });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoom, user.chatId, user.room]);

  useEffect(() => {
    if (
      currentRoom?.roomId === CHAT_LOADING_ID &&
      Array.isArray(rooms) &&
      rooms.length > 0
    ) {
      setCurrentRoom(rooms[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, currentRoom?.roomId]);

  const selectedRoomId = currentRoom?.roomId;
  const loadedRoom =
    selectedRoomId && selectedRoomId !== CHAT_LOADING_ID
      ? rooms?.find((r) => r.roomId === selectedRoomId)
      : undefined;

  const chatTabIsLoading =
    !isMinigamesTab &&
    (currentRoom?.roomId === CHAT_LOADING_ID ||
      (!!selectedRoomId &&
        !loadedRoom &&
        Array.isArray(rooms) &&
        rooms.length === 0));

  // Only inject loading tab if we expect chat but have no real chat room
  const displayedRooms = useMemo(() => {
    const list = Array.isArray(rooms) ? [...rooms] : [];
    const expectChat = !!selectedRoomId || !!user.chatId || !!user.room;
    const hasRealChatRoom = list.length > 0;

    if (expectChat && !hasRealChatRoom) {
      const id =
        loadedRoom?.roomId ||
        (selectedRoomId !== 'minigames' ? selectedRoomId : undefined) ||
        CHAT_LOADING_ID;
      const alreadyInList = id && list.some((r) => r.roomId === id);
      if (id && !alreadyInList) {
        list.unshift({
          roomId: id,
          name: text.t('chat.loadingTab', { defaultValue: 'Chat' }),
          isPrivate: false,
          __loading: !loadedRoom,
        });
      }
    }
    return list;
  }, [rooms, selectedRoomId, loadedRoom, user.chatId, user.room]);

  useEffect(() => {
    if (isMinigamesTab) {
      if (isLoading) setIsLoading(false);
      return;
    }
    if (!selectedRoomId || currentRoom?.roomId === CHAT_LOADING_ID) {
      if (!isLoading) setIsLoading(true);
      return;
    }
    if (loadedRoom) {
      if (isLoading) setIsLoading(false);
      if (loadedRoom.unreadCount > 0) {
        const index = rooms.findIndex((r) => r.roomId === loadedRoom.roomId);
        if (index !== -1) {
          resetUnreadCount(index);
          setLastUpdated(Date.now());
        }
      }
    } else if (!isLoading) {
      setIsLoading(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoomId, loadedRoom, isMinigamesTab, rooms, currentRoom?.roomId]);

  useEffect(() => {
    if (!isDesktop) {
      setIsChatExpanded(false);
      setBingoRoomsVisibility(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop]);

  const collectChatId = async () => {
    await api
      .get('/api/Bingo/rooms')
      .then((res) => {
        const activeRoom = res.find((room) => room.id === Number(user.room));
        setNumberOfBingoRooms(res.length);
        if (activeRoom) {
          if (!activeRoom.chatActive) setGroupChatIsClosed(true);
          setUser({ chatId: activeRoom.chatId });
        }
      })
      .catch((err) => {
        console.log('error on getting chat id: ', err);
      });
  };

  useEffect(() => {
    if (!user.bingocardId) {
      if (!user.chatId && user.room) collectChatId();
      if (
        (user.access_token && user.nickname && user.chatId) ||
        (!user.access_token && user.chatId)
      ) {
        if (!chatManager.current) chatManager.current = ChatManager.instance;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const sendDataLayer = () => {
    if (!window.dataLayer) return;
    window.dataLayer.push({
      event: 'ga4_event',
      ga4_data: {
        event_name: 'bingo_show_all_rooms',
        section: 'Bingo client',
        allow_auto_collect: true,
      },
    });
  };

  const onClickBingoRooms = () => {
    sendDataLayer();
    if (bingoRoomsIsVisible && loadedRoom) {
      const index = rooms.findIndex(
        (room) => room.roomId === loadedRoom.roomId
      );
      if (index !== -1) {
        resetUnreadCount(index);
        setLastUpdated(Date.now());
      }
    }
    setBingoRoomsVisibility(!bingoRoomsIsVisible);
  };

  const hideBingoRoomsButton = numberOfBingoRooms < 2 || !user.bingo_room_links;

  const { isPrivate } = loadedRoom || {};
  const currentRoomIdForTabs = isMinigamesTab
    ? 'minigames'
    : selectedRoomId || CHAT_LOADING_ID;

  return (
    <div
      ref={innerRef}
      className={clsx(styles.wrapper, {
        [styles.isExpanded]: isExpanded,
        [styles.isMinigamesTab]: isMinigamesTab && isExpanded,
      })}
    >
      {/* Bingo Rooms Button and Content */}
      {!isLoading && isDesktop && !hideBingoRoomsButton && !isMinigamesTab && (
        <div
          className={clsx(styles.bingoRoomsButton, {
            [styles.isDisplayingBingoRooms]: bingoRoomsIsVisible,
          })}
        >
          <Button isClean onClick={onClickBingoRooms}>
            <ArrowHead className={styles.arrow} />
            <span className={clsx({ [styles.isHidden]: bingoRoomsIsVisible })}>
              {text.t('chat.bingoRoomsButtonOpen')}
            </span>
            <span className={clsx({ [styles.isHidden]: !bingoRoomsIsVisible })}>
              {text.t('chat.bingoRoomsButtonClose')}
            </span>
          </Button>
          <ChatIcon
            className={clsx(styles.chatIcon, {
              [styles.isHidden]: !bingoRoomsIsVisible,
            })}
          />
        </div>
      )}
      <div
        className={clsx(styles.bingoRoomsContent, {
          [styles.isHidden]: !bingoRoomsIsVisible,
        })}
      >
        {bingoRoomsIsVisible && (
          <Scrollbars className={styles.scrollable}>
            <BingoRooms />
          </Scrollbars>
        )}
      </div>

      {/* Private Chat Requests */}
      {!isLoading &&
        activeRequests.length > 0 &&
        activeRequests.map((req) => (
          <PrivateChatReqModal
            key={req.requestId}
            request={req}
            cantBeAccepted={rooms.length >= 8}
          />
        ))}

      {!isLoading &&
        privateChatUserBlockModalMsg.length > 0 &&
        privateChatUserBlockModalMsg.map((message) => (
          <PrivateChatUserBlockModal key={message} blockMsg={message} />
        ))}

      {/* Toggle Button */}
      <ToggleButton isActive={isExpanded} onClick={onToggleMenu} />

      {/* Always mount MiniGamesList, only show when minigames tab is active */}
      <div style={{ display: isMinigamesTab ? 'block' : 'none' }}>
        <MiniGamesList miniGameListId={miniGameListId} />
      </div>

      {/* Tab Navigation: always show Chat (loading) and MiniGames if available */}
      {(displayedRooms.length > 0 || miniGameListId) && (
        <RoomList
          rooms={displayedRooms}
          miniGameListId={miniGameListId}
          isVisible={isExpanded}
          currentRoomId={currentRoomIdForTabs}
          onClick={onClickRoom}
          hasUserList={hasUserList}
        />
      )}

      {/* Tab Content: Chat (with spinner if loading) */}
      <div
        className={clsx(styles.content, {
          [styles.isHidden]: bingoRoomsIsVisible,
        })}
      >
        {!isMinigamesTab &&
          (chatTabIsLoading || isLoading ? (
            <Spinner />
          ) : (
            <>
              {isPrivate ? (
                <PrivateChatHeader
                  currentRoomId={loadedRoom?.roomId}
                  onClickReturn={onClickReturn}
                />
              ) : (
                <GroupChatHeader
                  currentRoomId={loadedRoom?.roomId}
                  cantRequest={rooms.length >= 8}
                  hasUserList={hasUserList}
                  setHasUserList={setHasUserList}
                />
              )}
              <MessageList
                currentRoomId={loadedRoom?.roomId}
                isPrivate={loadedRoom?.isPrivate}
              />
            </>
          ))}
      </div>
    </div>
  );
}

export default Chat;
